import React, {useContext, useEffect, useState} from "react";
import {Box, Button, Stack, Typography, useTheme} from "@mui/material";
import {CheckCircle} from "@mui/icons-material";
import {useMutation, useQuery} from "@tanstack/react-query";
import {FormProvider, useForm, useWatch} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useTranslation} from "react-i18next";
import {RecoilRoot, useRecoilValue, useSetRecoilState} from "recoil";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import CustomLoader from "../../../../util/component/CustomLoader";
import {ICustomerData, ICustomerDataForm, IOtpValidationForm, IPerson} from "../../models/models";
import DriverData from "../../create-ra/customer-data/driver-data/DriverData";
import {fillPersonPayloadData} from "../../create-ra/customer-data/CustomerData";
import {
	ADDITIONAL_DRIVER_INIT_VALUE, CUSTOMER_DATA_FORM_INITIAL_VALUES, CUSTOMER_DATA_FORM_VALIDATION_SCHEMA
} from "../../create-ra/customer-data/config";
import OtpValidationData from "../../create-ra/otp-validation/otp-validation-data/OtpValidationData";
import {OTP_VALIDATION_DATA_FORM_INITIAL_VALUES} from "../../create-ra/otp-validation/config";
import {
	bookingDataValueState, customerDataAccordionsState, customerDataValueState, driverResidenceCountriesListState,
	otpValidationValueState, prefixesListState
} from "../../create-ra/state";
import {
	fetchCountries, fetchPrefixes, fetchVerifyEmailMobileOtp, validateDocumentsByOwner
} from "../../create-ra/services/http.services";
import {checkPostSignDriver, savePostSignDriver} from "../services/http.services";
import {IPostSignDriverCheckResponse, IPostSignDriverResponse} from "./models";
import {isPostSignDriverReady, postSignDriverErrorMessage} from "./postSignAddRules";

export interface IPostSignAdditionalDriverProps {
	idRentalAgreement: string;
	title: string;
	/** cbId quando il guidatore e' pronto (registrato, controlli superati, OTP), altrimenti null. */
	onReadyChange: (cbId: string | null) => void;
	/** Rimozione dall'intestazione del form: toglie questa unita' dell'accessorio. */
	onRemove: () => void;
}

type TNotify = (message: string, type?: 0 | 1 | 2 | 3 | 4, params?: Record<string, string>) => void;

const useNotify = (): TNotify => {
	const {t} = useTranslation(['network']);
	const {dispatchGlobalData} = useContext(GlobalContext);
	return (message, type = snackbarTypes.error, params) => dispatchGlobalData({
		type: setSnackbar,
		payload: {snackbar: {message: t(message, params), type, show: true, autoClose: true}}
	});
};

/**
 * LPP-25233 — guidatore aggiuntivo venduto post-firma, gestito come in emissione: stesso form Dati cliente
 * (DriverData + DRIVER_VALIDATION_SCHEMA, ricerca cliente, upload e validazione documenti) e stessa verifica
 * OTP (OtpValidationData). Quei componenti leggono lo stato Recoil di create-ra: qui girano in un RecoilRoot
 * annidato e isolato, inizializzato come un'emissione con un solo guidatore aggiuntivo (slot `firstDriver`,
 * ind 0). L'emissione resta invariata e non condivide stato con il dettaglio RA.
 *
 * Sequenza: registra (POST .../post-sign/driver → cbId) → blacklist/MCTC (POST .../driver/check) → OTP.
 */
const PostSignAdditionalDriver = (props: IPostSignAdditionalDriverProps) => {
	const {t} = useTranslation(['network']);
	// Stesse liste dell'emissione (CreateRa), servono ai campi prefisso e nazione del form.
	const prefixesQuery = useQuery(['fetchPrefixes'], () => fetchPrefixes());
	const countriesQuery = useQuery(['fetchCountries'], () => fetchCountries());

	if (prefixesQuery.isError || countriesQuery.isError) {
		return <Typography color={'error'}>{t('customerData.message.error.tryLater')}</Typography>;
	}
	if (!prefixesQuery.data || !countriesQuery.data) {
		return <CustomLoader loading={true}/>;
	}
	return (
		<RecoilRoot initializeState={({set}) => {
			set(prefixesListState, prefixesQuery.data.map((elem) => ({code: elem.country, description: elem.prefix})));
			set(driverResidenceCountriesListState, countriesQuery.data.map((elem: any) => ({code: elem, description: elem})));
			set(bookingDataValueState, {
				idRentalAgreement: props.idRentalAgreement,
				bookingDetail: {flagSubstitution: false},
				financialInfo: {},
				firstDriver: {}
			});
			set(customerDataAccordionsState, {mainDriver: false, addDrivers: [true]});
			set(customerDataValueState, {additionalDrivers: []});
		}}>
			<DriverSection {...props}/>
		</RecoilRoot>
	);
};

const DriverSection = ({idRentalAgreement, title, onReadyChange, onRemove}: IPostSignAdditionalDriverProps) => {
	const theme = useTheme();
	const {t} = useTranslation(['network']);
	const notify = useNotify();
	const setBookingDataValue = useSetRecoilState(bookingDataValueState);
	const setOtpValidationValue = useSetRecoilState(otpValidationValueState);

	const [registeredCbId, setRegisteredCbId] = useState<string | null>(null);
	const [registeredDriver, setRegisteredDriver] = useState<IPerson | null>(null);
	const [registration, setRegistration] = useState<number>(0);
	const [checkPassed, setCheckPassed] = useState<boolean | null>(null);
	const [otpDone, setOtpDone] = useState<boolean>(false);

	const driverForm = useForm<ICustomerDataForm>({
		defaultValues: {...CUSTOMER_DATA_FORM_INITIAL_VALUES, additionalDrivers: [ADDITIONAL_DRIVER_INIT_VALUE]},
		// Stesso schema dell'emissione per il guidatore aggiuntivo, senza il titolare.
		resolver: yupResolver(CUSTOMER_DATA_FORM_VALIDATION_SCHEMA.omit(['mainDriver']), {}, {rawValues: true})
	});
	const [formCbId, verifiedDocuments] = useWatch({
		control: driverForm.control,
		name: ['additionalDrivers.0.cbId', 'additionalDrivers.0.verifiedDocuments']
	});

	const ready = isPostSignDriverReady({registeredCbId, formCbId, checkPassed, otpDone, verifiedDocuments});
	useEffect(() => {
		onReadyChange(ready ? registeredCbId : null);
	}, [ready, registeredCbId]);

	// Codici errore di sc-be uguali a quelli dell'emissione per il guidatore aggiuntivo 1 (create-ra/config.ts).
	const notifyDriverError = (err: unknown) => {
		const {key, params} = postSignDriverErrorMessage(err);
		notify(key, snackbarTypes.error, params);
	};

	const checkMutation = useMutation(checkPostSignDriver, {
		onSuccess: (response: IPostSignDriverCheckResponse) => {
			setCheckPassed(response.passed);
			if (!response.passed) {
				notify(response.blacklisted
					? 'raDetail.ancillaries.postSign.driver.blacklisted'
					: 'raDetail.ancillaries.postSign.driver.drivingLicenseInvalid');
			}
		},
		onError: (err) => notifyDriverError(err)
	});

	const toPerson = (d: ICustomerData): IPerson => ({...fillPersonPayloadData(d), type: 'DRIVER'});

	const registerMutation = useMutation((d: ICustomerData) => savePostSignDriver({
		driver: JSON.stringify(toPerson(d)),
		idRentalAgreement,
		idDocFront: d.idDocFront ?? null,
		idDocBack: d.idDocBack ?? null,
		drivingLicenseDocFront: d.drivingLicenseDocFront ?? null,
		drivingLicenseDocBack: d.drivingLicenseDocBack ?? null
	}), {
		onSuccess: (response: IPostSignDriverResponse, d: ICustomerData) => {
			const driver: IPerson = {...toPerson(d), cbId: response.cbId};
			driverForm.setValue('additionalDrivers.0.cbId', response.cbId);
			driverForm.setValue('additionalDrivers.0.customerID', response.customerId ?? '');
			driverForm.setValue('additionalDrivers.0.verifiedDocuments', response.validDocuments);
			// Come dopo updateDraftRA: DriverData (reload) e OtpValidationData leggono il guidatore dallo slot firstDriver.
			setBookingDataValue((prev: any) => ({...prev, firstDriver: driver}));
			setOtpValidationValue((prev: any) => ({...prev, additionalDrivers: [{email: undefined, mobile: undefined}]}));
			setRegisteredDriver(driver);
			setRegisteredCbId(response.cbId);
			setRegistration((n) => n + 1);
			setCheckPassed(null);
			setOtpDone(false);
			checkMutation.mutate({idRentalAgreement, cbId: response.cbId});
		},
		onError: (err) => {
			// Come CustomerData: CF non valido segnalato anche sul campo.
			const data = (err as any)?.response?.data;
			if (Array.isArray(data) && data.some((e: any) => e?.code === 'additional_driver_1_fiscal_code')) {
				driverForm.setError('additionalDrivers.0.fiscalCode', {message: 'common.error.invalidFS'});
			}
			notifyDriverError(err);
		}
	});

	const validateDocumentsMutation = useMutation((cbId: string) => validateDocumentsByOwner(cbId), {
		onSuccess: () => {
			driverForm.setValue('additionalDrivers.0.verifiedDocuments', true);
			notify('customerData.message.success', snackbarTypes.success);
		},
		onError: () => notify('customerData.message.error.tryLater')
	});

	const handleRegister = driverForm.handleSubmit(
		(data: ICustomerDataForm) => registerMutation.mutate(data.additionalDrivers[0]),
		() => notify('common.error.formDataIsInvalid')
	);

	return (
		<Box data-testid={'post-sign-additional-driver'} sx={{mt: theme.spacing(1)}}>
			<FormProvider {...driverForm}>
				<DriverData title={title} referenceProp={'additionalDrivers'} ind={0}
							deleteAdditionalDriver={onRemove}
							validateDocuments={(cbId: string) => validateDocumentsMutation.mutate(cbId)}/>
			</FormProvider>

			<Stack direction={'row'} spacing={2} alignItems={'center'} justifyContent={'flex-end'} sx={{mt: theme.spacing(1)}}>
				{
					ready &&
					<Stack direction={'row'} spacing={1} alignItems={'center'}>
						<CheckCircle color={'success'}/>
						<Typography variant={'body'}>{t('raDetail.ancillaries.postSign.driver.ready')}</Typography>
					</Stack>
				}
				<Button variant={'outlined'} color={'accent'} data-testid={'post-sign-driver-register'}
						disabled={registerMutation.isLoading} onClick={handleRegister}>
					{t('raDetail.ancillaries.postSign.driver.register')}
				</Button>
			</Stack>

			{
				checkPassed === true && !!registeredDriver && String(formCbId ?? '') === registeredCbId &&
				<PostSignDriverOtp key={registration} idRentalAgreement={idRentalAgreement} driver={registeredDriver}
								   onDone={() => setOtpDone(true)}/>
			}

			<CustomLoader loading={registerMutation.isLoading || checkMutation.isLoading || validateDocumentsMutation.isLoading}/>
		</Box>
	);
};

/**
 * Verifica OTP come in emissione (OtpValidation): verifyemailandmobile manda i codici ai contatti non
 * certificati, OtpValidationData li invia/verifica; "salta la convalida" e "conferma driver validi" chiudono
 * il passo esattamente come in emissione.
 */
const PostSignDriverOtp = ({idRentalAgreement, driver, onDone}: {idRentalAgreement: string, driver: IPerson, onDone: () => void}) => {
	const theme = useTheme();
	const {t} = useTranslation(['network']);
	const notify = useNotify();
	const otpValidationValue = useRecoilValue(otpValidationValueState);
	const setOtpValidationValue = useSetRecoilState(otpValidationValueState);

	const mobile = '+' + driver.prefix?.description + driver.mobile;
	const otpForm = useForm<IOtpValidationForm>({
		defaultValues: {
			...OTP_VALIDATION_DATA_FORM_INITIAL_VALUES,
			additionalDrivers: [{name: driver.firstName, surname: driver.lastName, email: driver.email, mobile, emailOtp: '', mobileOtp: ''}]
		}
	});

	const verifyMutation = useMutation((payload: Record<string, string>) => fetchVerifyEmailMobileOtp(payload), {
		onSuccess: (response: any) => {
			const email = response.firstDriverIsEmailCertified;
			const mobileCertified = response.firstDriverIsMobileCertified;
			setOtpValidationValue((prev: any) => ({...prev, additionalDrivers: [{email, mobile: mobileCertified}]}));
			if (!(email && mobileCertified)) {
				notify('otpValidation.message.warning.toValidate', snackbarTypes.info);
			}
		},
		onError: () => notify('customerData.message.error.tryLater')
	});

	useEffect(() => {
		verifyMutation.mutate({
			idRentalAgreement,
			firstDriverCbId: String(driver.cbId),
			firstDriverEmail: driver.email,
			firstDriverMobile: mobile,
			firstDriverFullName: driver.firstName + ' ' + driver.lastName
		});
	}, []);

	const certified = otpValidationValue?.additionalDrivers?.[0]?.email === true
		&& otpValidationValue?.additionalDrivers?.[0]?.mobile === true;
	useEffect(() => {
		if (certified) {
			onDone();
		}
	}, [certified]);

	return (
		<Box data-testid={'post-sign-driver-otp'} sx={{mt: theme.spacing(2)}}>
			<FormProvider {...otpForm}>
				<OtpValidationData title={t('otpValidation.form.title.additionalDriver')} referenceProp={'additionalDrivers'} ind={0}/>
			</FormProvider>
			<Box sx={{display: 'flex', justifyContent: 'center', mt: theme.spacing(2)}}>
				<Button color={'accent'} onClick={onDone} sx={{mx: theme.spacing(1)}}>
					{t('otpValidation.form.button.skipValidation')}
				</Button>
				<Button color={'accent'} onClick={onDone} sx={{mx: theme.spacing(1)}}>
					{t('otpValidation.form.button.confirmValidDrivers')}
				</Button>
			</Box>
			<CustomLoader loading={verifyMutation.isLoading}/>
		</Box>
	);
};

export default PostSignAdditionalDriver;
